import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge } from '@mono/ui-components';
import { Crown, Eye, User, UserMinus } from 'lucide-react';
import { useState } from 'react';
import { MemberRoleSelect } from './MemberRoleSelect';
import { RemoveMemberDialog } from './RemoveMemberDialog';
/**
 * Get role badge variant and icon
 */ function getRoleBadgeConfig(role) {
    switch(role){
        case 'owner':
            return {
                variant: 'default',
                icon: Crown,
                className: 'bg-indigo-500 text-white',
                label: 'Owner'
            };
        case 'member':
            return {
                variant: 'secondary',
                icon: User,
                className: 'bg-gray-500 text-white',
                label: 'Member'
            };
        case 'view_only':
            return {
                variant: 'outline',
                icon: Eye,
                className: 'border-gray-400 text-gray-700',
                label: 'View Only'
            };
    }
}
export function MemberList({ project, currentUserId }) {
    const [hoveredMemberId, setHoveredMemberId] = useState(null);
    const [removeMemberId, setRemoveMemberId] = useState(null);
    const members = project.members || [];
    const currentMember = members.find((m)=>m.userId === currentUserId);
    const isOwner = currentMember?.role === 'owner';
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-2",
                "data-testid": "member-list",
                children: members.map((member)=>{
                    const isCurrentUser = member.userId === currentUserId;
                    const roleBadge = getRoleBadgeConfig(member.role);
                    const RoleIcon = roleBadge.icon;
                    const canModify = isOwner && member.role !== 'owner' && !isCurrentUser;
                    return /*#__PURE__*/ _jsx("div", {
                        className: "flex items-center justify-between p-3 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors",
                        "data-testid": `member-item-${member.userId}`,
                        onMouseEnter: ()=>setHoveredMemberId(member.userId),
                        onMouseLeave: ()=>setHoveredMemberId(null),
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-3 flex-1",
                            children: [
                                /*#__PURE__*/ _jsx("img", {
                                    src: `https://avatar.vercel.sh/${btoa(member.email)}`,
                                    alt: member.name,
                                    className: "w-10 h-10 rounded-full"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex-1",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "flex items-center gap-2",
                                            children: /*#__PURE__*/ _jsxs("p", {
                                                className: "text-sm font-medium text-gray-900",
                                                children: [
                                                    member.name,
                                                    isCurrentUser && /*#__PURE__*/ _jsx("span", {
                                                        className: "ml-2 text-xs text-gray-500 font-normal",
                                                        children: "(you)"
                                                    })
                                                ]
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs text-gray-500",
                                            children: member.email
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsxs(Badge, {
                                            variant: roleBadge.variant,
                                            className: `text-xs ${roleBadge.className}`,
                                            children: [
                                                /*#__PURE__*/ _jsx(RoleIcon, {
                                                    className: "w-3 h-3 mr-1 inline",
                                                    "aria-hidden": "true"
                                                }),
                                                roleBadge.label
                                            ]
                                        }),
                                        canModify && member.role !== 'owner' && /*#__PURE__*/ _jsx(MemberRoleSelect, {
                                            projectId: project.projectId,
                                            member: member,
                                            currentRole: member.role
                                        })
                                    ]
                                }),
                                canModify && hoveredMemberId === member.userId && /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: ()=>setRemoveMemberId(member.userId),
                                    className: "ml-2 p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors",
                                    "aria-label": `Remove ${member.name} from project`,
                                    "data-testid": `remove-member-button-${member.userId}`,
                                    children: /*#__PURE__*/ _jsx(UserMinus, {
                                        className: "w-4 h-4"
                                    })
                                })
                            ]
                        })
                    }, member.userId);
                })
            }),
            removeMemberId && (()=>{
                const memberToRemove = members.find((m)=>m.userId === removeMemberId);
                // Guard against member not found (shouldn't happen but satisfies type checker)
                if (!memberToRemove) return null;
                return /*#__PURE__*/ _jsx(RemoveMemberDialog, {
                    open: !!removeMemberId,
                    onOpenChange: (open)=>!open && setRemoveMemberId(null),
                    projectId: project.projectId,
                    member: memberToRemove
                });
            })()
        ]
    });
}
